import { languages, type Language } from '@aikoa/i18n'
import { useTranslations } from '@aikoa/i18n/react'
import { useMemo } from 'react'
import { cn } from '~/lib/utils'
import { Select, SelectContent, SelectItem, SelectTrigger } from './ui/select'
import { Text } from './ui/text'

/* eslint-disable i18next/no-literal-string */
const languageEmojis: Record<Language, string> = {
  fi: '🇫🇮',
  en: '🇬🇧',
  es: '🇪🇸',
  sv: '🇸🇪',
}
/* eslint-enable i18next/no-literal-string */

interface LanguageInputProps {
  value: Language
  onChange: (locale: Language) => void
  className?: string
  triggerClassName?: string
  iconClassName?: string
  showLabel?: boolean
  align?: 'start' | 'center' | 'end'
}

export function LanguageInput({
  value,
  onChange,
  className,
  triggerClassName,
  iconClassName,
  showLabel = false,
  align = 'end',
}: LanguageInputProps) {
  const { t } = useTranslations('common')

  const languageNames: Record<Language, string> = useMemo(
    () => ({
      fi: t(($) => $.languageSelector.options.fi),
      en: t(($) => $.languageSelector.options.en),
      es: t(($) => $.languageSelector.options.es),
      sv: t(($) => $.languageSelector.options.sv),
    }),
    [t]
  )

  const currentOption = useMemo(
    () => ({
      value,
      label: languageNames[value],
    }),
    [value, languageNames]
  )

  const handleLanguageChange = (option: { value: string; label: string } | undefined) => {
    if (option?.value && languages.includes(option.value as Language)) {
      onChange(option.value as Language)
    }
  }

  const sortedLocales = useMemo(
    () =>
      (Object.keys(languageEmojis) as Language[]).filter((locale) => languages.includes(locale)),
    []
  )

  return (
    <Select value={currentOption} onValueChange={handleLanguageChange} className={className}>
      <SelectTrigger
        iconClassName={cn('text-tertiary-foreground', iconClassName)}
        className={cn(
          'h-10 items-center gap-2 border-0 bg-transparent px-3 py-2 text-sm outline-none',
          triggerClassName
        )}>
        <Text className="text-lg">{languageEmojis[value]}</Text>
        {showLabel && <Text className="text-sm">{languageNames[value]}</Text>}
      </SelectTrigger>
      <SelectContent className="p-1" sideOffset={10} align={align} listClassName="gap-1">
        {sortedLocales.map((locale) => (
          <SelectItem
            key={locale}
            value={locale}
            label={`${languageEmojis[locale]}  ${languageNames[locale]}`}
            className="mt-1 rounded-md first:mt-0"
          />
        ))}
      </SelectContent>
    </Select>
  )
}
